import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import { restoreAuth } from './redux/reducers/authSlice'
import { loadSettingsFromStorage } from './redux/reducers/settingsSlice'
import webSocketService from './services/websocket'

// Pages
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Monitoring from './pages/Monitoring'
import Settings from './pages/Settings'
import AdminManagement from './pages/AdminManagement'
import UserManagement from './pages/UserManagement'
import UserDetails from './pages/UserDetails'
import OrganizationManagement from './pages/OrganizationManagement'
import OrganizationDetails from './pages/OrganizationDetails'
import CommunicationManagement from './pages/CommunicationManagement'
import ClearPrintingQueue from './pages/ClearPrintingQueue'

// Layout
import AppLayout from './components/layout/AppLayout'
import ProtectedRoute from './components/ProtectedRoute'

function App() {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const { darkMode } = useAppSelector((state) => state.settings)
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    // Restore auth from localStorage on app load
    dispatch(restoreAuth())
    
    // Load saved settings
    dispatch(loadSettingsFromStorage())
    
    // Mark auth as checked after restoration
    setAuthChecked(true)
  }, [dispatch])

  useEffect(() => {
    // Set dispatch for WebSocket service
    webSocketService.setDispatch(dispatch)
    
    // Connect WebSocket when authenticated
    if (isAuthenticated) {
      webSocketService.connect()
    } else {
      webSocketService.disconnect()
    }
    
    return () => {
      // Cleanup on unmount
      webSocketService.disconnect()
    }
  }, [isAuthenticated, dispatch])

  useEffect(() => {
    // Apply dark mode class to HTML element
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])

  // Don't render routes until auth has been checked
  if (!authChecked) {
    return <div>Loading...</div>
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="users/:userId" element={<UserDetails />} />
          <Route path="organizations" element={<OrganizationManagement />} />
          <Route path="organizations/:organizationId" element={<OrganizationDetails />} />
          <Route path="communications" element={<CommunicationManagement />} />
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="admins" element={<AdminManagement />} />
          <Route path="print-server/clear-queue" element={<ClearPrintingQueue />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        
        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App